(ns common.schema.entities.tenant
  (:require
   [common.currencies :as currencies]
   [common.modules :as modules]
   [common.schema.schema-utils :as su]
   [common.security.password-complexity :as password-complexity]))

(def modus-operandi-options
  [{:key   :modus-operandi/gp
    :label "GP in a GP/LP setting"}
   {:key   :modus-operandi/lp
    :label "LP"}
   {:key   :modus-operandi/corporate
    :label "Corporate (M&A, CVC, etc)"}
   {:key   :modus-operandi/family-office
    :label "Family office"}
   {:key   :modus-operandi/incubator
    :label "Incubator/accelerator"}
   {:key   :modus-operandi/business-angel
    :label "Business angel"}
   {:key   :modus-operandi/broker
    :label "Broker/banker/matchmaker"}
   {:key   :modus-operandi/crowdfunding-operator
    :label "Crowdfunding operator"}
   {:key   :modus-operandi/consultant
    :label "Consultant"}
   {:key   :modus-operandi/other
    :label "Other"}])

(def investment-industry-focus-options
  [{:key   :investment-industry-focus/ict
    :label "ICT"}
   {:key   :investment-industry-focus/fintech
    :label "FinTech"}
   {:key   :investment-industry-focus/health
    :label "Health"}
   {:key   :investment-industry-focus/pharma
    :label "Pharma"}])

(def investment-stage-options
  [{:key   :investment-stage/early
    :label "Early"}
   {:key   :investment-stage/growth
    :label "Growth"}
   {:key   :investment-stage/buyout
    :label "Buyout"}])

(def investment-geography-focus-options
  [{:key   :investment-geography-focus/global
    :label "Global"}
   {:key   :investment-geography-focus/europe
    :label "Europe"}
   {:key   :investment-geography-focus/pacific
    :label "Pacific"}
   {:key   :investment-geography-focus/usa-canada
    :label "USA/Canada"}
   {:key   :investment-geography-focus/middle-east
    :label "EMEA"}
   {:key   :investment-geography-focus/africa
    :label "Africa"}])

(def aum-options
  [{:key   :assets/not-applicable
    :label "N/A"}
   {:key   :assets/option-1
    :label "Less than $20M"}
   {:key   :assets/option-2
    :label "$20M ... $99M"}
   {:key   :assets/option-3
    :label "$100M ... $999M"}
   {:key   :assets/option-4
    :label "$1B or more"}])

(def kpi-wizard-status-options
  [{:key   :wizard-finished
    :label "Finished"}])

(def auth-options
  [{:key   :authentication.type/password-only
    :label "Password only"}
   {:key   :authentication.type/optional-2FA
    :label "2FA optional"}
   {:key   :authentication.type/mandatory-2FA
    :label "2FA mandatory"}])

(def nev-2023-options
  [{:key   :not-available
    :label "Not available"}
   {:key   :available
    :label "Available"}
   {:key   :forced
    :label "Forced"}])

(def schema
  (vec
   (concat
    (su/name-unique-global :tenant/name "Name" {:authorization su/access-zadmin-ssg})
    (su/integer-field :tenant/tenant-id-number
                      "Number"
                      {:authorization    su/access-system-zadmin
                       :system-semantics su/system-controlled-required-field-semantics
                       :system-required? true})
    (su/text-simple-field :tenant/domain-primary
                          "Primary domain"
                          {:authorization su/access-admin-user})
    (su/text-simple-multi-field :tenant/domains "Domains" {:authorization su/access-admin-user})

    (su/text-unique-global :tenant/custom-login-path
                           "Custom login path"
                           {:authorization su/access-zadmin-zadmin
                            :editor-type   :editor.type/text-simple})
    (su/boolean-field :tenant/block-password-login
                      "Block password login"
                      {:authorization su/access-zadmin-user})
    (su/image-field :tenant/logo "Tenant logo" {:authorization su/access-admin-ssg})
    (su/text-edn-field :tenant/field-configurations
                       "Field configuration"
                       {:authorization su/access-admin-ssg})
    (su/text-edn-field :tenant/product-plan "Product plan" {:authorization su/access-zadmin-ssg})
    (su/text-edn-field :tenant/feature-settings
                       "Feature settings"
                       {:authorization su/access-zadmin-ssg})
    (su/keyword-field :tenant/default-currency
                      "Default currency"
                      currencies/options
                      {:authorization su/access-admin-ssg})
    (su/long-field :tenant/session-timeout "Session timeout" {:authorization su/access-admin-admin})
    (su/keyword-field :tenant/addin-session-timeout-kw
                      "Add-in session timeout"
                      nil  ;; options provided by common.session-timeout/timeout-options
                      {:authorization su/access-admin-user})
    (su/keyword-field :tenant/web-session-timeout-kw
                      "Web session timeout"
                      nil  ;; options provided by common.session-timeout/timeout-options
                      {:authorization su/access-admin-user})
    (su/keyword-field :tenant/addin-max-connection-lifetime-kw
                      "Add-in maximum connection lifetime"
                      nil  ;; options provided by common.connection-lifetime/lifetime-options
                      {:authorization su/access-admin-user})
    (su/keyword-field :tenant/mcp-max-connection-lifetime-kw
                      "MCP maximum connection lifetime"
                      nil  ;; options provided by common.connection-lifetime/lifetime-options
                      {:authorization su/access-admin-user})
    (su/boolean-field :tenant/mcp-allow-write?
                      "Allow MCP connections to have write access — when disabled (default) all MCP connections are read-only"
                      {:authorization su/access-admin-user})
    (su/single-ref-field :tenant/ai-default-service
                         "Default AI service"
                         [:entity.type/ai-service]
                         {:authorization su/access-admin-admin})
    (su/text-simple-field :tenant/payment-method "Payment method" {})
    (su/keyword-multi-field :tenant/whitelisted-custom-fields
                            "Whitelisted field"
                            nil
                            {:authorization su/access-admin-ssg})
    (su/keyword-multi-field :tenant/blacklisted-fields
                            "Blacklisted field"
                            nil
                            {:authorization su/access-admin-ssg})
    (su/keyword-multi-field :tenant/fields-in-use
                            "Fields in use"
                            nil
                            {:authorization su/access-admin-ssg})
    (su/text-simple-field :tenant/date-format "Date format" {:authorization su/access-admin-ssg})
    (su/boolean-field :tenant/has-sandbox?
                      "Has sandbox?"
                      {:authorization    su/access-system-ssg
                       :system-semantics su/system-controlled-optional-field-semantics})
    (su/keyword-field :tenant/kpi-category-wizard-status
                      "KPI wizard status"
                      kpi-wizard-status-options
                      {:authorization    su/access-system-ssg
                       :system-semantics su/system-controlled-optional-field-semantics})
    (su/keyword-field :tenant/authentication-setting
                      "Authentication type"
                      auth-options
                      {:authorization su/access-admin-ssg})
    (su/keyword-field :tenant/modus-operandi
                      "Modus operandi"
                      modus-operandi-options
                      {:authorization su/access-admin-admin})
    (su/keyword-field :tenant/investment-industry-focus
                      "Investment industry focus"
                      investment-industry-focus-options
                      {:authorization su/access-admin-admin})
    (su/keyword-field :tenant/investment-geography-focus
                      "Investment geography focus"
                      investment-geography-focus-options
                      {:authorization su/access-admin-admin})
    (su/keyword-field :tenant/investment-stage
                      "Investment stage"
                      investment-stage-options
                      {:authorization su/access-admin-admin})
    (su/keyword-field :tenant/assets-under-management
                      "AUM"
                      aum-options
                      {:authorization su/access-admin-admin})
    (su/keyword-multi-field :tenant/modules
                            "Modules"
                            modules/options
                            {:authorization su/access-zadmin-user})
    (su/text-simple-field :tenant/timezone "Timezone" {:authorization su/access-admin-ssg})

    (su/boolean-field :tenant/has-custom-tos
                      "Has custom terms of service?"
                      {:authorization su/access-sys-admin-user})
    (su/boolean-field :tenant/sandbox?
                      "Is sandbox tenant?"
                      {:authorization    su/access-system-ssg
                       :system-semantics su/system-controlled-optional-field-semantics})
    (su/boolean-field :tenant/sandbox-disabled?
                      "Is sandbox disabled?"
                      {:authorization su/access-admin-ssg})
    (su/boolean-field :tenant/tenant-onboarding?
                      "Onboarding needed?"
                      {:authorization    su/access-system-user
                       :system-semantics su/system-controlled-optional-field-semantics})
    (su/boolean-field :tenant/tenant-initialized?
                      "Is initialized?"
                      {:authorization    su/access-system-user
                       :system-semantics su/system-controlled-optional-field-semantics})
    (su/text-simple-field :tenant/last-migration
                          "Las migration"
                          {:authorization su/access-sys-admin-sys-admin})
    (su/text-simple-field :tenant/technical-contact-email
                          "Technical contact email"
                          {:authorization su/access-admin-admin})
    (su/text-simple-field :tenant/cur-multiplier
                          "Currency multiplier"
                          {:authorization su/access-admin-bsg})
    (su/text-multiline-field :tenant/put-on-hold-template
                             "Put on hold template"
                             {:authorization su/access-bsg-bsg})
    (su/text-simple-field :tenant.inbox/email "Email inbox" {:authorization su/access-zadmin-bsg})
    (su/url-field :tenant/white-labelled-bsg-logo
                  "White-labelled logo BSG"
                  {:authorization su/access-admin-ssg})
    (su/text-simple-field :tenant/white-labelled-bsg-navbar-color
                          "Navbar color"
                          {:authorization su/access-admin-ssg})
    (su/text-simple-field :tenant/white-labelled-bsg-navbar-text-color
                          "Navbar text color"
                          {:authorization su/access-admin-ssg})
    (su/keyword-multi-field :tenant/password-complexity
                            "Password complexity"
                            password-complexity/complexity-options
                            {:authorization su/access-admin-ssg})
    (su/integer-field :tenant/password-min-length
                      "Password length"
                      {:authorization su/access-admin-ssg
                       :constraints   {:constraint/greater-than-inclusive 8
                                       :constraint/smaller-than-inclusive 50}})
    (su/keyword-multi-field :tenant/sso-logins
                            "SSO login methods"
                            nil
                            {:authorization    su/access-system-ssg
                             :system-semantics su/system-controlled-optional-field-semantics})       ;; modified on REPL console only
    (su/email-field :tenant/kyc-sender-email
                    "KYC sender email"
                    {:authorization    su/access-system-system
                     :system-semantics su/system-controlled-optional-field-semantics})               ;; modified on REPL console only
    (su/email-field :tenant/lp-reporting-sender-email
                    "LP reporting sender email"
                    {:authorization su/access-zadmin-zadmin})                                       ;; modified on REPL console only
    (su/email-field :tenant/reporting-sender-email
                    "Reporting sender email"
                    {:authorization su/access-zadmin-zadmin})                                       ;; modified on REPL console only
    (su/keyword-multi-field :tenant/active-kyc-templates
                            "Active KYC templates"
                            nil
                            {:authorization    su/access-system-user
                             :system-semantics su/system-controlled-optional-field-semantics})       ;; modified on REPL console only

    (su/keyword-multi-field :tenant/active-tenant-kyc-templates
                            "Active Tenant KYC templates"
                            nil
                            {:authorization    su/access-system-user
                             :system-semantics su/system-controlled-optional-field-semantics})       ;; modified on REPL console only

    (su/keyword-multi-field :tenant/active-contract-kyc-templates
                            "Active Contract KYC templates"
                            nil
                            {:authorization    su/access-system-user
                             :system-semantics su/system-controlled-optional-field-semantics})       ;; modified on REPL console only

    ;; Set as system level field for now since not used.
    ;; Use admin-admin or admin-user if enabled later.
    (su/boolean-field :tenant/automated-kyc-renewal-enabled?
                      "Automated KYC renewal enabled"
                      {:authorization su/access-system-system})

    (su/text-edn-field :tenant/automated-kyc-renewal-configuration
                       "Automated KYC renewal configuration"
                       {:authorization su/access-admin-admin})

    (su/text-simple-field :tenant/customer-success-manager-email
                          "Customer success manager email"
                          {:authorization su/access-admin-user})
    (su/text-simple-field :tenant/account-manager-email
                          "Account manager email"
                          {:authorization su/access-admin-user})
    (su/text-simple-field :tenant/first-closer-email
                          "First closer email"
                          {:authorization su/access-admin-user})

    (su/url-field :tenant/saml-idp-post-url "IDP post URL" {:authorization su/access-admin-admin})
    (su/url-field :tenant/saml-idp-redirect-url
                  "IDP redirect URL"
                  {:authorization su/access-admin-admin})
    (su/url-field :tenant/saml-idp-logout-url
                  "IDP logout URL"
                  {:authorization su/access-admin-admin})
    (su/text-simple-field :tenant/saml-idp-entity-id
                          "IDP entity ID"
                          {:authorization su/access-admin-admin})
    (su/text-multiline-field :tenant/saml-idp-public-key
                             "IDP public key"
                             {:authorization su/access-admin-admin})

    ;; Premium feature, cannot be turned on from UI - only developer through REPL
    ;; this should not be turned for existing tenants unless a migration is implemented DEV-4142
    (su/text-simple-field :tenant/custom-s3-bucket
                          "Custom S3 bucket"
                          {:authorization    su/access-system-system
                           :system-semantics su/system-controlled-optional-field-semantics})

    (su/text-simple-field :tenant/azure-sso-tenant-id
                          "Azure SSO tenant ID"
                          {:authorization su/access-admin-admin})
    (su/text-simple-field :tenant/azure-sso-client-secret
                          "Azure SSO client secret"
                          {:authorization su/access-admin-system})
    (su/text-simple-field :tenant/azure-sso-client-id
                          "Azure SSO client ID"
                          {:authorization su/access-admin-admin})
    (su/text-simple-field :tenant/azure-sso-authority
                          "Azure SSO authority"
                          {:authorization su/access-admin-admin})
    (su/text-simple-field :tenant/azure-sso-scope
                          "Azure SSO scope"
                          {:authorization su/access-admin-admin})
    (su/text-simple-field :tenant/custom-login-logo-url
                          "Custom login logo url"
                          {:authorization    su/access-system-system
                           :system-semantics su/system-controlled-optional-field-semantics})
    (su/text-simple-field :tenant/custom-login-banner-img-url
                          "Custom login banner image url"
                          {:authorization    su/access-system-system
                           :system-semantics su/system-controlled-optional-field-semantics})
    (su/text-simple-field :tenant/custom-login-banner-img-xl-url
                          "Custom login banner image url XL"
                          {:authorization    su/access-system-system
                           :system-semantics su/system-controlled-optional-field-semantics})
    (su/text-simple-field :tenant/custom-login-color
                          "Custom login highlight text color"
                          {:authorization    su/access-system-system
                           :system-semantics su/system-controlled-optional-field-semantics})
    (su/text-simple-field :tenant/custom-login-button-text-color
                          "Custom login button text color"
                          {:authorization    su/access-system-system
                           :system-semantics su/system-controlled-optional-field-semantics})
    (su/text-simple-field :tenant/custom-login-forgot-password-img-url
                          "Custom login forgot password image"
                          {:authorization    su/access-system-system
                           :system-semantics su/system-controlled-optional-field-semantics})
    (su/integer-field :tenant/default-currency-decimal-places
                      "Decimal places for currency fields"
                      {:authorization su/access-admin-ssg})

    (su/text-edn-field :tenant/preferences "Preferences" {:authorization su/access-admin-ssg})
    (su/text-edn-field :tenant/field-schemas
                       "Field schemas"
                       {:authorization    su/access-system-ssg
                        :system-semantics su/system-controlled-optional-field-semantics})
    (su/integer-field :tenant/plan-updated-on
                      "Plan updated on"
                      {:authorization    su/access-system-ssg
                       :system-semantics su/system-controlled-optional-field-semantics})
    (su/multi-ref-field :tenant/custom-field-definitions
                        "Custom fields"
                        [:entity.type/custom-field]
                        {:authorization    su/access-system-ssg
                         :system-semantics su/system-controlled-optional-field-semantics
                         :default-field-visibility :in-use})
    (su/text-edn-field :tenant/entity-creation-config
                       "Entity Creation Config edn data."
                       {:authorization su/access-admin-user})

    (su/integer-field :tenant/max-number-of-login-page-configs
                      "Max number of custom login pages"
                      {:authorization su/access-zadmin-admin})

;; LP Portal configs

    (su/text-simple-field :tenant/reporting-domain
                          "LP reporting domain -> Used to identify correct tenant"
                          {:authorization su/access-zadmin-zadmin})
    (su/text-simple-field :tenant/reporting-domain-alt
                          "LP reporting domain (2) -> Used to identify correct tenant"
                          {:authorization su/access-zadmin-zadmin})

    (su/keyword-field :tenant/reporting-theme
                      "Reporting theme"
                      nil
                      {:authorization su/access-system-ssg})
    (su/image-field :tenant/portal-background-image
                    "Portal background image"
                    {:authorization su/access-zadmin-ssg})

    (su/keyword-multi-field :tenant/reporting-fund-templates
                            "Reporting fund templates"
                            nil
                            {:authorization su/access-zadmin-ssg})
    (su/keyword-multi-field :tenant/reporting-holding-templates
                            "Reporting holding templates"
                            nil
                            {:authorization su/access-zadmin-ssg})
    (su/image-field :tenant/reporting-logo-grey
                    "Reporting logo - Gray"
                    {:authorization su/access-zadmin-ssg})
    (su/image-field :tenant/reporting-logo-white
                    "Reporting logo - White"
                    {:authorization su/access-zadmin-ssg})
    (su/email-field :tenant/reporting-contact-email
                    "Reporting contact email"
                    {:authorization su/access-zadmin-ssg})
    (su/url-field :tenant/reporting-news-url
                  "Reporting news site URL"
                  {:authorization su/access-zadmin-ssg})
    (su/keyword-field :tenant/reporting-news-source
                      "Reporting news source"
                      nil
                      {:authorization    su/access-system-system
                       :system-semantics su/system-controlled-optional-field-semantics})
    (su/multi-ref-field :tenant/lp-portal-news-types
                        "LP-portal news types"
                        [:entity.type/lp-news-type]
                        {:authorization su/access-zadmin-lpg
                         :default-field-visibility :in-use})
    (su/multi-ref-field :tenant/lp-portal-news-categories
                        "LP-portal news categories"
                        [:entity.type/lp-news-category]
                        {:authorization su/access-zadmin-lpg
                         :default-field-visibility :in-use})

    ;; Screening
    (su/text-simple-field :tenant/risk-screen-api-key
                          "RiskScreen API key"
                          {:authorization su/access-admin-admin})

    (su/text-edn-field :tenant/esign-api-credentials
                       "eSign API credentials"
                       {:authorization su/access-admin-user})

    (su/boolean-field :tenant/esign-v2?
                      "E-sign v2?"
                      {:authorization su/access-zadmin-user})

    (su/keyword-field :tenant/nev-2023
                      "NEV 2023"
                      nev-2023-options
                      {:authorization su/access-zadmin-user})

    ;; Vaults
    (su/text-simple-field :tenant/vault-domain
                          "Vault domain"
                          {:authorization su/access-admin-user})

    ;; Surveys
    (su/text-simple-field :tenant/survey-domain
                          "Survey domain"
                          {:authorization su/access-admin-user})
    (su/text-simple-field :tenant/survey-logo
                          "Survey default logo"
                          {:authorization su/access-admin-none})
    (su/text-simple-field :tenant/survey-banner-image
                          "Survey login page background image"
                          {:authorization su/access-admin-none})
    (su/text-simple-field :tenant/survey-email-sender
                          "Survey email sender"
                          {:authorization su/access-admin-user})
    (su/text-simple-field :tenant/survey-email-template
                          "Survey email template"
                          {:authorization su/access-admin-user})
    (su/text-simple-field :tenant/login-email-template
                          "Login email template"
                          {:authorization su/access-admin-user})

    ; Decisions
    (su/text-edn-field :tenant/decision-configuration
                       "Decision configuration"
                       {:authorization su/access-admin-user})
    (su/multi-ref-field :tenant/investment-reservation-types
                        "Investment reservation types"
                        [:entity.type/investment-reservation-type]
                        {:authorization su/access-admin-user
                         :default-field-visibility :in-use})

    ;; Counterparty diligence
    (su/email-field :tenant/counterparty-diligence-sender-email
                    "Counterparty diligence sender email"
                    {:authorization    su/access-system-system
                     :system-semantics su/system-controlled-optional-field-semantics})

    (su/text-edn-field :tenant/counterparty-diligence-config
                       "Counterparty diligence configuration"
                       {:authorization su/access-admin-user})

    ;; Transaction process
    (su/email-field :tenant/transaction-process-sender-email
                    "Transaction process notification sender email"
                    {:authorization    su/access-system-system
                     :system-semantics su/system-controlled-optional-field-semantics})

    ;; default login page
    (su/single-ref-field :tenant/default-login-page-config
                         "Default login page config"
                         [:entity.type/login-page-config]
                         {:authorization su/access-admin-user})

    ;; Dedicated
    (su/boolean-field :tenant/suspended "Suspended - no login, no emails" {:authorization su/access-zadmin-user})
    (su/boolean-field :tenant/marked-for-excision "Can be permanently deleted" {:authorization su/access-zadmin-zadmin})
    (su/text-simple-field :tenant/kyc-redirect-url "Redirect to KYC" {:authorization su/access-zadmin-zadmin})

    (su/text-simple-field :tenant/reporting-db-type
                          "Reporting DB type"
                          {:authorization    su/access-system-admin
                           :system-semantics su/system-controlled-optional-field-semantics})
    (su/text-simple-field :tenant/reporting-db-database-name
                          "Reporting DB database name"
                          {:authorization    su/access-system-admin
                           :system-semantics su/system-controlled-optional-field-semantics})
    (su/text-simple-field :tenant/reporting-db-write-host
                          "Reporting DB write host"
                          {:authorization    su/access-system-system
                           :system-semantics su/system-controlled-optional-field-semantics})
    (su/text-simple-field :tenant/reporting-db-write-port
                          "Reporting DB write port"
                          {:authorization    su/access-system-system
                           :system-semantics su/system-controlled-optional-field-semantics})
    (su/text-simple-field :tenant/reporting-db-write-user
                          "Reporting DB write username"
                          {:authorization    su/access-system-system
                           :system-semantics su/system-controlled-optional-field-semantics})
    (su/text-simple-field :tenant/reporting-db-write-password
                          "Reporting DB write password"
                          {:authorization    su/access-system-system
                           :system-semantics su/system-controlled-optional-field-semantics})
    (su/text-simple-field :tenant/reporting-db-read-host
                          "Reporting DB read host"
                          {:authorization    su/access-system-admin
                           :system-semantics su/system-controlled-optional-field-semantics})
    (su/text-simple-field :tenant/reporting-db-read-port
                          "Reporting DB read port"
                          {:authorization    su/access-system-admin
                           :system-semantics su/system-controlled-optional-field-semantics})
    (su/text-simple-field :tenant/reporting-db-read-username
                          "Reporting DB read username"
                          {:authorization    su/access-system-admin
                           :system-semantics su/system-controlled-optional-field-semantics})
    (su/text-simple-field :tenant/reporting-db-read-password
                          "Reporting DB read password"
                          {:authorization    su/access-system-system
                           :system-semantics su/system-controlled-optional-field-semantics})
    (su/datetime-field :tenant/reporting-db-last-synced-on
                       "Last completed sync"
                       {:authorization    su/access-system-system
                        :no-history?      true
                        :system-semantics su/system-controlled-optional-field-semantics})
    (su/integer-field :tenant/reporting-db-connection-fail-count
                      "# of consequent connection failures"
                      {:authorization    su/access-system-system
                       :system-semantics su/system-controlled-optional-field-semantics})
    (su/datetime-field :tenant/field-configuration-changed-on
                       "Last field config change"
                       {:authorization    su/access-system-system
                        :system-semantics su/system-controlled-optional-field-semantics})
    (su/datetime-field :tenant/reindexing-requested-on
                       "Re-indexing requested on"
                       {:authorization    su/access-system-system
                        :no-history?      true
                        :system-semantics su/system-controlled-optional-field-semantics})
    (su/datetime-field :tenant/reindexing-started-on
                       "Re-indexing started on"
                       {:authorization    su/access-system-system
                        :no-history?      true
                        :system-semantics su/system-controlled-optional-field-semantics})
    (su/datetime-field :tenant/universal-index-epoch
                       "Universal index last fully rebuilt on"
                       {:authorization    su/access-system-system
                        :no-history?      true
                        :system-semantics su/system-controlled-optional-field-semantics})
    (su/integer-field :tenant/scraping-processed "Scraping completed year" {:authorization su/access-admin-admin})
    (su/single-ref-field :tenant/default-vault-config
                         "Vault config defaults"
                         [:entity.type/vault-config]
                         {:authorization    su/access-system-user
                          :system-semantics su/system-controlled-required-field-semantics
                          :system-required? true})
    (su/datetime-field :tenant/dedicated-reindexing-requested-on
                       "Dedicated indexes re-indexing requested on"
                       {:authorization    su/access-system-system
                        :no-history?      true
                        :system-semantics su/system-controlled-optional-field-semantics})
    (su/datetime-field :tenant/dedicated-reindexing-started-on
                       "Dedicated indexes re-indexing started on"
                       {:authorization    su/access-system-system
                        :no-history?      true
                        :system-semantics su/system-controlled-optional-field-semantics})
    (su/datetime-field :tenant/dedicated-index-epoch
                       "Dedicated indexes last fully rebuilt on"
                       {:authorization    su/access-system-system
                        :no-history?      true
                        :system-semantics su/system-controlled-optional-field-semantics})
    (su/long-field :tenant/dedicated-reindexing-failure-count
                   "Dedicated indexes re-indexing consecutive failure count"
                   {:authorization    su/access-system-system
                    :system-semantics su/system-controlled-optional-field-semantics}))))
